import { useState, useRef, useCallback } from "react";
import { useGymEmailConfig } from "./useGymEmailConfig";
import {
  DEFAULT_INACTIVITY_DAYS,
  MAX_INACTIVITY_DAYS,
  MIN_INACTIVITY_DAYS,
  type GymEmailConfig,
  type UpdateGymEmailConfigInput,
} from "../../../utils/zod/admin";

export type InactivitySectionState = GymEmailConfig["inactivityReminders"];

// El input se valida nativo, pero con la sección apagada no se renderiza y
// un valor inválido tumbaría el guardado completo.
const clampInactivityDays = (days: number): number =>
  Number.isInteger(days)
    ? Math.min(MAX_INACTIVITY_DAYS, Math.max(MIN_INACTIVITY_DAYS, days))
    : DEFAULT_INACTIVITY_DAYS;

const defaultInactivity: InactivitySectionState = {
  enabled: false,
  time: "",
  subject: "",
  inactiveDays: DEFAULT_INACTIVITY_DAYS,
};

export interface AdminSectionState {
  enabled: boolean;
  time: string;
  subject: string;
  recipients: string[];
  newRecipient: string;
  expiringSoonDays: number;
  recentlyExpiredDays: number;
}

export interface BasicSectionState {
  enabled: boolean;
  time: string;
  subject: string;
  recipients: string[];
  newRecipient: string;
}

export interface RemindersSectionState extends BasicSectionState {
  excludePaymentMethodKeys: string[];
}

export interface LowStockAlertsSectionState {
  enabled: boolean;
  subject: string;
  recipients: string[];
  newRecipient: string;
}

export type EmailSectionKey =
  | "admin"
  | "sales"
  | "reminders"
  | "lowStockAlerts";

const defaultAdmin: AdminSectionState = {
  enabled: false,
  time: "",
  subject: "",
  recipients: [],
  newRecipient: "",
  expiringSoonDays: 3,
  recentlyExpiredDays: 7,
};

const defaultSales: BasicSectionState = {
  enabled: false,
  time: "",
  subject: "",
  recipients: [],
  newRecipient: "",
};

const defaultReminders: RemindersSectionState = {
  enabled: false,
  time: "",
  subject: "",
  recipients: [],
  newRecipient: "",
  excludePaymentMethodKeys: [],
};

const defaultLowStockAlerts: LowStockAlertsSectionState = {
  enabled: false,
  subject: "",
  recipients: [],
  newRecipient: "",
};

const fromConfig = (
  config: GymEmailConfig,
): {
  admin: AdminSectionState;
  sales: BasicSectionState;
  reminders: RemindersSectionState;
  lowStockAlerts: LowStockAlertsSectionState;
} => ({
  admin: {
    enabled: config.adminReport.enabled,
    time: config.adminReport.time,
    subject: config.adminReport.subject,
    recipients: config.adminReport.recipients,
    newRecipient: "",
    expiringSoonDays: config.adminReport.config?.expiringSoonDays ?? 3,
    recentlyExpiredDays: config.adminReport.config?.recentlyExpiredDays ?? 7,
  },
  sales: {
    enabled: config.salesReport.enabled,
    time: config.salesReport.time,
    subject: config.salesReport.subject,
    recipients: config.salesReport.recipients,
    newRecipient: "",
  },
  reminders: {
    enabled: config.subscriptionReminders.enabled,
    time: config.subscriptionReminders.time,
    subject: config.subscriptionReminders.subject,
    recipients: config.subscriptionReminders.recipients,
    newRecipient: "",
    excludePaymentMethodKeys:
      config.subscriptionReminders.exclude?.paymentMethodKeys ?? [],
  },
  lowStockAlerts: {
    enabled: config.lowStockAlerts.enabled,
    subject: config.lowStockAlerts.subject,
    recipients: config.lowStockAlerts.recipients,
    newRecipient: "",
  },
});

export const useGymEmailConfigForm = () => {
  const { emailConfig, isLoading, isSaving, updateConfig, refreshConfig } =
    useGymEmailConfig();

  const [adminReport, setAdminReportState] =
    useState<AdminSectionState>(defaultAdmin);
  const [salesReport, setSalesReportState] =
    useState<BasicSectionState>(defaultSales);
  const [reminders, setRemindersState] =
    useState<RemindersSectionState>(defaultReminders);
  const [lowStockAlerts, setLowStockAlertsState] =
    useState<LowStockAlertsSectionState>(defaultLowStockAlerts);
  const [paymentFailedEnabled, setPaymentFailedEnabled] = useState(true);
  const [inactivity, setInactivityState] =
    useState<InactivitySectionState>(defaultInactivity);

  const lastConfigRef = useRef<GymEmailConfig | null>(null);
  if (emailConfig && emailConfig !== lastConfigRef.current) {
    lastConfigRef.current = emailConfig;
    const parsed = fromConfig(emailConfig);
    setAdminReportState(parsed.admin);
    setSalesReportState(parsed.sales);
    setRemindersState(parsed.reminders);
    setLowStockAlertsState(parsed.lowStockAlerts);
    setPaymentFailedEnabled(emailConfig.paymentFailed.enabled);
    setInactivityState(emailConfig.inactivityReminders);
  }
  if (!emailConfig) lastConfigRef.current = null;

  const setAdminReport = useCallback(
    (patch: Partial<AdminSectionState>) =>
      setAdminReportState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setSalesReport = useCallback(
    (patch: Partial<BasicSectionState>) =>
      setSalesReportState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setReminders = useCallback(
    (patch: Partial<RemindersSectionState>) =>
      setRemindersState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setInactivity = useCallback(
    (patch: Partial<InactivitySectionState>) =>
      setInactivityState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const setLowStockAlerts = useCallback(
    (patch: Partial<LowStockAlertsSectionState>) =>
      setLowStockAlertsState((prev) => ({ ...prev, ...patch })),
    [],
  );

  const addRecipient = useCallback(
    (section: EmailSectionKey) => {
      if (section === "admin") {
        const trimmed = adminReport.newRecipient.trim();
        if (trimmed && !adminReport.recipients.includes(trimmed)) {
          setAdminReportState((prev) => ({
            ...prev,
            recipients: [...prev.recipients, trimmed],
            newRecipient: "",
          }));
        }
      } else if (section === "sales") {
        const trimmed = salesReport.newRecipient.trim();
        if (trimmed && !salesReport.recipients.includes(trimmed)) {
          setSalesReportState((prev) => ({
            ...prev,
            recipients: [...prev.recipients, trimmed],
            newRecipient: "",
          }));
        }
      } else if (section === "reminders") {
        const trimmed = reminders.newRecipient.trim();
        if (trimmed && !reminders.recipients.includes(trimmed)) {
          setRemindersState((prev) => ({
            ...prev,
            recipients: [...prev.recipients, trimmed],
            newRecipient: "",
          }));
        }
      } else {
        const trimmed = lowStockAlerts.newRecipient.trim();
        if (trimmed && !lowStockAlerts.recipients.includes(trimmed)) {
          setLowStockAlertsState((prev) => ({
            ...prev,
            recipients: [...prev.recipients, trimmed],
            newRecipient: "",
          }));
        }
      }
    },
    [adminReport, salesReport, reminders, lowStockAlerts],
  );

  const removeRecipient = useCallback(
    (section: EmailSectionKey, email: string) => {
      if (section === "admin") {
        setAdminReportState((prev) => ({
          ...prev,
          recipients: prev.recipients.filter((r) => r !== email),
        }));
      } else if (section === "sales") {
        setSalesReportState((prev) => ({
          ...prev,
          recipients: prev.recipients.filter((r) => r !== email),
        }));
      } else if (section === "reminders") {
        setRemindersState((prev) => ({
          ...prev,
          recipients: prev.recipients.filter((r) => r !== email),
        }));
      } else {
        setLowStockAlertsState((prev) => ({
          ...prev,
          recipients: prev.recipients.filter((r) => r !== email),
        }));
      }
    },
    [],
  );

  const addExcludeKey = useCallback((key: string) => {
    setRemindersState((prev) => {
      if (prev.excludePaymentMethodKeys.includes(key)) return prev;
      return {
        ...prev,
        excludePaymentMethodKeys: [...prev.excludePaymentMethodKeys, key],
      };
    });
  }, []);

  const removeExcludeKey = useCallback((key: string) => {
    setRemindersState((prev) => ({
      ...prev,
      excludePaymentMethodKeys: prev.excludePaymentMethodKeys.filter(
        (k) => k !== key,
      ),
    }));
  }, []);

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    const input: UpdateGymEmailConfigInput = {
      adminReport: {
        enabled: adminReport.enabled,
        time: adminReport.time,
        subject: adminReport.subject,
        recipients: adminReport.recipients,
        config: {
          expiringSoonDays: adminReport.expiringSoonDays,
          recentlyExpiredDays: adminReport.recentlyExpiredDays,
        },
      },
      salesReport: {
        enabled: salesReport.enabled,
        time: salesReport.time,
        subject: salesReport.subject,
        recipients: salesReport.recipients,
      },
      subscriptionReminders: {
        enabled: reminders.enabled,
        time: reminders.time,
        subject: reminders.subject,
        recipients: reminders.recipients,
        exclude: { paymentMethodKeys: reminders.excludePaymentMethodKeys },
      },
      lowStockAlerts: {
        enabled: lowStockAlerts.enabled,
        subject: lowStockAlerts.subject,
        recipients: lowStockAlerts.recipients,
      },
      paymentFailed: { enabled: paymentFailedEnabled },
      inactivityReminders: {
        ...inactivity,
        inactiveDays: clampInactivityDays(inactivity.inactiveDays),
      },
    };

    const result = await updateConfig(input);
    if (result) await refreshConfig();
    return result;
  }, [
    adminReport,
    salesReport,
    reminders,
    lowStockAlerts,
    paymentFailedEnabled,
    inactivity,
    updateConfig,
    refreshConfig,
  ]);

  return {
    emailConfig,
    isLoading,
    isSaving,
    adminReport,
    setAdminReport,
    salesReport,
    setSalesReport,
    reminders,
    setReminders,
    lowStockAlerts,
    setLowStockAlerts,
    paymentFailedEnabled,
    setPaymentFailedEnabled,
    inactivity,
    setInactivity,
    addRecipient,
    removeRecipient,
    addExcludeKey,
    removeExcludeKey,
    handleSubmit,
  };
};
